import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import PaymentModal from './PaymentModal';

const MB_PER_VIDEO_MINUTE = 20;
const MINUTES_PER_REFLECTION = 5;
const MB_PER_REFLECTION = MB_PER_VIDEO_MINUTE * MINUTES_PER_REFLECTION; // 100 MB per 5-min video

function formatCents(cents) {
  if (cents == null) return 'Free';
  return `$${(cents / 100).toFixed(0)}`;
}

function formatStorage(bytes) {
  if (!bytes) return null;
  const gb = bytes / (1024 ** 3);
  return gb >= 1 ? `${Math.round(gb)} GB` : `${Math.round(bytes / (1024 ** 2))} MB`;
}

// Returns a clean rounded number string, e.g. "2,500" or "10,000"
function estimateVideoReflections(bytes) {
  if (!bytes) return null;
  const count = Math.floor(bytes / (1024 * 1024 * MB_PER_REFLECTION));
  let rounded;
  if (count >= 5000)      rounded = Math.floor(count / 1000) * 1000;
  else if (count >= 1000) rounded = Math.floor(count / 500) * 500;
  else if (count >= 200)  rounded = Math.floor(count / 100) * 100;
  else                    rounded = Math.floor(count / 10) * 10;
  return rounded.toLocaleString();
}

function PlanComparisonCard({ currentSubscription, onSubscriptionChange }) {
  const [plans, setPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/subscriptions/plans')
      .then(r => setPlans(r.data))
      .catch(err => console.error('Failed to load plans:', err))
      .finally(() => setLoading(false));
  }, []);

  const currentPlanId = currentSubscription?.planId;

  const handleSelect = (plan) => {
    if (plan.id === currentPlanId) return;
    if (!plan.price_monthly_cents) {
      // Downgrade to Free — no payment needed
      api.post('/subscriptions/subscribe', { planId: plan.id, paymentMethod: 'none' })
        .then(r => onSubscriptionChange && onSubscriptionChange(r.data))
        .catch(err => alert('Failed to switch plan: ' + (err.response?.data?.error || err.message)));
      return;
    }
    setSelectedPlan(plan);
  };

  const getButtonLabel = (plan) => {
    if (plan.id === currentPlanId) return 'Current Plan';
    if (!plan.price_monthly_cents) return 'Downgrade to Free';
    const currentPlan = plans.find(p => p.id === currentPlanId);
    if (currentPlan && plan.display_order > currentPlan.display_order) return 'Upgrade';
    return 'Change Plan';
  };

  const getPrice = (plan) => {
    if (!plan.price_monthly_cents) return 'Free';
    if (billingPeriod === 'yearly' && plan.price_yearly_cents) {
      return `${formatCents(Math.round(plan.price_yearly_cents / 12))}/mo`;
    }
    return `${formatCents(plan.price_monthly_cents)}/mo`;
  };

  const getYearlySavings = (plan) => {
    if (!plan.price_yearly_cents || !plan.price_monthly_cents) return null;
    const monthlyTotal = plan.price_monthly_cents * 12;
    const savings = monthlyTotal - plan.price_yearly_cents;
    return Math.round((savings / monthlyTotal) * 100);
  };

  if (loading) return <div className="loading-spinner" />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <span style={{ opacity: billingPeriod === 'monthly' ? 1 : 0.5, cursor: 'pointer' }}
          onClick={() => setBillingPeriod('monthly')}>Monthly</span>
        <div
          onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
          style={{
            width: '44px', height: '24px', borderRadius: '12px',
            background: billingPeriod === 'yearly' ? 'var(--color-primary, #4a90d9)' : '#ccc',
            position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
          }}
        >
          <div style={{
            width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
            position: 'absolute', top: '2px',
            left: billingPeriod === 'yearly' ? '22px' : '2px',
            transition: 'left 0.2s',
          }} />
        </div>
        <span style={{ opacity: billingPeriod === 'yearly' ? 1 : 0.5, cursor: 'pointer' }}
          onClick={() => setBillingPeriod('yearly')}>Annual <span style={{ color: 'green', fontSize: '0.8em' }}>Save up to 32%</span></span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${plans.length}, 1fr)`, gap: '1rem' }}>
        {plans.map(plan => {
          const isCurrent = plan.id === currentPlanId;
          const savingsPct = getYearlySavings(plan);
          const features = Array.isArray(plan.features_json) ? plan.features_json : [];

          return (
            <div
              key={plan.id}
              style={{
                border: isCurrent ? '2px solid var(--color-primary, #4a90d9)' : '1px solid var(--border-color, #ddd)',
                borderRadius: '8px',
                padding: '1.25rem',
                position: 'relative',
                background: isCurrent ? 'var(--color-primary-subtle, #f0f7ff)' : undefined,
              }}
            >
              {isCurrent && (
                <div style={{
                  position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                  background: 'var(--color-primary, #4a90d9)', color: '#fff',
                  padding: '2px 12px', borderRadius: '10px', fontSize: '0.75em', whiteSpace: 'nowrap',
                }}>Current Plan</div>
              )}

              <h3 style={{ margin: '0 0 0.25rem' }}>{plan.name}</h3>

              <div style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                {getPrice(plan)}
              </div>

              {billingPeriod === 'yearly' && savingsPct && (
                <div style={{ fontSize: '0.8em', color: 'green', marginBottom: '0.5rem' }}>
                  Save {savingsPct}% vs monthly
                </div>
              )}

              {plan.price_yearly_cents && billingPeriod === 'yearly' && (
                <div style={{ fontSize: '0.8em', opacity: 0.7, marginBottom: '0.75rem' }}>
                  Billed {formatCents(plan.price_yearly_cents)}/year
                </div>
              )}

              {plan.max_family_members && (
                <div style={{ fontSize: '0.85em', opacity: 0.8, marginBottom: '0.75rem' }}>
                  Includes {plan.max_family_members} members
                  {plan.extra_member_monthly_cents && ` · +${formatCents(plan.extra_member_monthly_cents)}/mo each`}
                </div>
              )}

              {/* Storage indicator */}
              {plan.storage_limit_bytes && (
                <div style={{ fontSize: '0.8em', marginBottom: '0.75rem', opacity: 0.8 }}>
                  {plan.max_reflections ? (
                    // Reflection-capped plan: storage isn't the real gate, just show the size
                    <span>{formatStorage(plan.storage_limit_bytes)} storage included</span>
                  ) : (
                    // Storage-gated plan: show estimate as a positive framing
                    <span>
                      {formatStorage(plan.storage_limit_bytes)} —&nbsp;
                      <span style={{ color: 'var(--color-success, #16a34a)', fontWeight: '500' }}>
                        room for ~{estimateVideoReflections(plan.storage_limit_bytes)} 5-minute video reflections
                      </span>
                      <sup>†</sup>
                    </span>
                  )}
                </div>
              )}

              <hr style={{ margin: '0.75rem 0', opacity: 0.2 }} />

              <ul style={{ paddingLeft: '1.2rem', margin: '0 0 1rem', fontSize: '0.875em', lineHeight: 1.6 }}>
                {features.map((f, i) => <li key={i}>{f}</li>)}
              </ul>

              <button
                onClick={() => handleSelect(plan)}
                disabled={isCurrent}
                className={isCurrent ? '' : 'btn-save'}
                style={{
                  width: '100%',
                  opacity: isCurrent ? 0.5 : 1,
                  cursor: isCurrent ? 'default' : 'pointer',
                }}
              >
                {getButtonLabel(plan)}
              </button>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: '0.72em', opacity: 0.5, marginTop: '1rem', textAlign: 'center' }}>
        † Estimated at {MINUTES_PER_REFLECTION} minutes per video at standard quality (~{MB_PER_VIDEO_MINUTE} MB/min). Audio and text reflections use far less space.
      </p>

      {selectedPlan && (
        <PaymentModal
          plan={selectedPlan}
          billingPeriod={billingPeriod}
          onClose={() => setSelectedPlan(null)}
          onSuccess={(data) => {
            setSelectedPlan(null);
            onSubscriptionChange && onSubscriptionChange(data);
          }}
        />
      )}
    </div>
  );
}

export default PlanComparisonCard;
